import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { RadioCard, Tooltip } from "@intility/bifrost-react";
import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Icon from "@intility/bifrost-react/Icon";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Radio from "@intility/bifrost-react/Radio";
import { useEffect, useState } from "react";
import type {
  CreateResourceTypeRequest,
  ResourceType,
  UpdateResourceTypeRequest,
} from "~/api/fga-types";
import { slugify } from "~/lib/clientTypes";
import {
  composeDynamicRegex,
  deriveSegmentTemplate,
  ID_PATTERNS,
  type IdPatternKey,
  isValidPlaceholder,
  literalValue,
} from "~/lib/resourceType";

export interface ResourceTypeCreateFormProps {
  onSubmit: (req: CreateResourceTypeRequest) => Promise<unknown>;
  onSuccess?: () => void;
  onError?: (err: Error) => void;
  submitLabel?: string;
  showDescription?: boolean;
  autoSlug?: boolean;
  verbose?: boolean;
  editType?: ResourceType;
}

/**
 * The nameRegex value for an edit-mode submit. `undefined` means "keep the
 * existing regex" (the server only overwrites the field when it is present);
 * a value replaces it. A dynamic type whose regex has no named capture group
 * cannot be round-tripped through the builder fields, so `generatedRegex` is
 * undefined there; sending "" instead would clear the stored regex.
 */
export function editNameRegexPayload(
  generatedRegex: string | undefined,
  existing: string | null | undefined,
): string | undefined {
  if (!generatedRegex) return undefined;
  return generatedRegex === (existing ?? undefined)
    ? undefined
    : generatedRegex;
}

function parseBuilderFields(regex: string): {
  prefix: string;
  placeholder: string;
  idPattern: IdPatternKey;
  customPattern: string;
} | null {
  const m = regex.match(/^\^(.*?)\(\?<([A-Za-z_][A-Za-z0-9_]*)>(.*?)\)\$$/);
  if (!m) return null;
  const rawPrefix = m[1];
  const placeholder = m[2];
  const pattern = m[3];
  const prefix = rawPrefix.replace(/\\([.*+?^${}()|[\]\\])/g, "$1");
  for (const [key, val] of Object.entries(ID_PATTERNS)) {
    if (key !== "custom" && val.pattern === pattern) {
      return {
        prefix,
        placeholder,
        idPattern: key as IdPatternKey,
        customPattern: "",
      };
    }
  }
  return {
    prefix,
    placeholder,
    idPattern: "custom" as IdPatternKey,
    customPattern: pattern,
  };
}

export default function ResourceTypeCreateForm({
  onSubmit,
  onSuccess,
  onError,
  submitLabel,
  showDescription = true,
  autoSlug = false,
  verbose = true,
  editType,
}: ResourceTypeCreateFormProps) {
  const isEdit = editType !== undefined;
  const effectiveSubmitLabel = submitLabel ?? (isEdit ? "Save" : "Create");

  const [slug, setSlug] = useState(isEdit ? editType.slug : "");
  const [slugTouched, setSlugTouched] = useState(false);
  const [displayName, setDisplayName] = useState(
    isEdit ? editType.displayName : "",
  );
  const [description, setDescription] = useState(
    isEdit ? (editType.description ?? "") : "",
  );
  const [cardinality, setCardinality] = useState<"single" | "many">(
    isEdit ? (editType.isStatic ? "single" : "many") : "many",
  );

  const parsed =
    isEdit && editType.nameRegex && !editType.isStatic
      ? parseBuilderFields(editType.nameRegex)
      : null;

  const fallbackCustom =
    isEdit && editType.nameRegex && !editType.isStatic && !parsed
      ? editType.nameRegex.replace(/^\^/, "").replace(/\$$/, "")
      : "";

  const [placeholder, setPlaceholder] = useState(parsed?.placeholder ?? "");
  const [literalPrefix, setLiteralPrefix] = useState(parsed?.prefix ?? "");
  const [idPattern, setIdPattern] = useState<IdPatternKey>(
    parsed ? parsed.idPattern : fallbackCustom ? "custom" : "guid",
  );
  const [customPattern, setCustomPattern] = useState(
    parsed?.customPattern ?? fallbackCustom,
  );
  const [fixedName, setFixedName] = useState(
    isEdit && editType.isStatic && editType.nameRegex
      ? literalValue(editType.nameRegex)
      : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [dynHelpOpen, setDynHelpOpen] = useState(false);

  const builderPattern =
    idPattern === "custom"
      ? customPattern.trim()
      : ID_PATTERNS[idPattern].pattern;

  const generatedRegex =
    cardinality === "single"
      ? `^${fixedName.trim() || slug.trim()}$`
      : placeholder.trim()
        ? composeDynamicRegex(
            literalPrefix.trim(),
            placeholder.trim(),
            builderPattern,
          )
        : undefined;

  const previewSegment = generatedRegex
    ? deriveSegmentTemplate(generatedRegex)
    : null;

  const canSubmit = isEdit
    ? displayName.trim() &&
      (cardinality === "single" ||
        !placeholder.trim() ||
        (isValidPlaceholder(placeholder.trim()) && builderPattern.length > 0))
    : slug.trim() &&
      displayName.trim() &&
      (cardinality === "single" ||
        !placeholder.trim() ||
        (isValidPlaceholder(placeholder.trim()) && builderPattern.length > 0));

  useEffect(() => {
    if (autoSlug && !slugTouched) {
      setSlug(slugify(displayName));
    }
  }, [displayName, autoSlug, slugTouched]);

  const handleSubmit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        const req: UpdateResourceTypeRequest = {
          displayName:
            displayName.trim() !== editType.displayName
              ? displayName.trim()
              : undefined,
          description:
            description.trim() !== (editType.description ?? "")
              ? description.trim()
              : undefined,
          nameRegex: editNameRegexPayload(generatedRegex, editType.nameRegex),
        };
        await onSubmit(req as CreateResourceTypeRequest);
      } else {
        await onSubmit({
          slug: slug.trim(),
          displayName: displayName.trim(),
          description: showDescription
            ? description.trim() || undefined
            : undefined,
          nameRegex: generatedRegex,
          isStatic: cardinality === "single",
        });
      }
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError?.(err instanceof Error ? err : new Error(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid gap={12}>
      {isEdit && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <code>{editType.slug}</code>
          {editType.isStatic ? (
            <Badge state="neutral">Static</Badge>
          ) : (
            <Badge state="chill">Dynamic</Badge>
          )}
        </div>
      )}
      <Message
        state="neutral"
        expandable
        header="Creating a type"
        isOpen={helpOpen}
        onHeaderClick={() => setHelpOpen(!helpOpen)}
      >
        Use clear, general names since resource types are shared across
        platforms. Slugs should be kebab-case (e.g. <code>server</code>,{" "}
        <code>cost-center</code>).
      </Message>
      <Input
        label="Display name"
        placeholder={verbose ? "e.g. Server" : "Location"}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      {!isEdit && (
        <Input
          label="Slug"
          placeholder={
            verbose ? "e.g. server" : "location (lowercase kebab-case)"
          }
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }}
        />
      )}
      {showDescription && (
        <Input
          label="Description"
          optional
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      )}
      {!isEdit && (
        <fieldset
          className="bf-fieldset"
          style={{ margin: 0, padding: 0, border: "none" }}
        >
          <legend className="bf-label">Static or dynamic?</legend>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <RadioCard
              name="resource-type-cardinality"
              label="Static"
              checked={cardinality === "single"}
              onChange={() => setCardinality("single")}
            >
              {verbose && (
                <span
                  style={{
                    fontSize: "0.875em",
                    color: "var(--bfc-base-c-2)",
                  }}
                >
                  A single fixed node. The path segment will be the slug itself.
                </span>
              )}
            </RadioCard>
            <RadioCard
              name="resource-type-cardinality"
              label="Dynamic"
              checked={cardinality === "many"}
              onChange={() => setCardinality("many")}
            >
              {verbose && (
                <span
                  style={{
                    fontSize: "0.875em",
                    color: "var(--bfc-base-c-2)",
                  }}
                >
                  Many instances, each identified by a unique value (GUID, slug,
                  etc.).
                </span>
              )}
            </RadioCard>
          </div>
        </fieldset>
      )}
      {cardinality === "single" && (
        <Input
          label="Path segment"
          placeholder={slug || "e.g. tenants"}
          value={fixedName}
          onChange={(e) => setFixedName(e.target.value)}
          feedback={
            !fixedName.trim() && !slug.trim()
              ? "Defaults to the slug if left empty"
              : undefined
          }
          optional
        />
      )}
      {cardinality === "many" && (
        <>
          <Message
            state="neutral"
            expandable
            header="Dynamic types"
            isOpen={dynHelpOpen}
            onHeaderClick={() => setDynHelpOpen(!dynHelpOpen)}
          >
            <p>
              <strong>Variable name:</strong> the key developers use to pass an
              ID in code (e.g. <code>{"{ serverId: 'srv-123' }"}</code>). The
              SDK maps each name to the correct position in the path.
            </p>
            <p>
              <strong>ID format:</strong> all instances of this type must use
              the same format so paths stay predictable (e.g. always GUIDs, not
              a mix of GUIDs and freeform names).
            </p>
            <p style={{ margin: 0 }}>
              <strong>Path prefix:</strong> we highly recommend using a prefix.
              If two dynamic types share the same ID format, a prefix tells them
              apart in the path (e.g. <code>srv-</code> for servers and{" "}
              <code>comp-</code> for computers, both followed by a GUID).
            </p>
          </Message>
          {isEdit && fallbackCustom && !placeholder.trim() && (
            <Message state="neutral" noIcon>
              Current regex: <code>{editType.nameRegex}</code>
            </Message>
          )}
          <Input
            label="Variable name"
            placeholder={verbose ? "e.g. serverId" : "tenantId"}
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            feedback={
              placeholder.trim() && !isValidPlaceholder(placeholder.trim())
                ? "Letters, digits and underscore; cannot start with a digit"
                : undefined
            }
            state={
              placeholder.trim() && !isValidPlaceholder(placeholder.trim())
                ? "alert"
                : undefined
            }
          />
          {placeholder.trim() && (
            <>
              <fieldset
                className="bf-fieldset"
                style={{ margin: 0, padding: 0, border: "none" }}
              >
                <legend className="bf-label">ID format</legend>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  {(Object.keys(ID_PATTERNS) as IdPatternKey[]).map((key) => (
                    <Tooltip key={key} content={ID_PATTERNS[key].description}>
                      <Radio
                        label={ID_PATTERNS[key].label}
                        name="id-format"
                        checked={idPattern === key}
                        onChange={() => setIdPattern(key)}
                      />
                    </Tooltip>
                  ))}
                </div>
              </fieldset>
              {idPattern === "custom" && (
                <Input
                  label="Custom pattern"
                  placeholder="e.g. [a-z0-9]{6}"
                  value={customPattern}
                  onChange={(e) => setCustomPattern(e.target.value)}
                />
              )}
              <Input
                label="Path prefix"
                optional
                description={
                  verbose
                    ? "Highly recommended. Distinguishes this type from siblings sharing the same ID format (e.g. srv- vs comp-)."
                    : "Optional literal prefix baked into the path"
                }
                placeholder="e.g. srv-"
                value={literalPrefix}
                onChange={(e) => setLiteralPrefix(e.target.value)}
              />
            </>
          )}
          {generatedRegex && (
            <Message state="brand" noIcon>
              Preview: <code>{previewSegment ?? generatedRegex}</code>
            </Message>
          )}
          {!generatedRegex && cardinality === "many" && (
            <Message state="neutral" noIcon>
              Any value accepted (no regex) — path segment placeholder is
              derived from the slug.
            </Message>
          )}
        </>
      )}
      {cardinality === "single" && generatedRegex && (
        <Message state="brand" noIcon>
          Preview: <code>{previewSegment ?? generatedRegex}</code>
        </Message>
      )}
      {error && (
        <Message state="alert" header="Error">
          {error}
        </Message>
      )}
      <Button
        variant="filled"
        state={busy || !canSubmit ? "inactive" : undefined}
        onClick={handleSubmit}
      >
        {busy ? "Saving…" : effectiveSubmitLabel}
        {!busy && <Icon icon={faArrowRight} marginLeft />}
      </Button>
    </Grid>
  );
}
